import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { FilterOption } from '@/scopes/search/types'

// eslint-disable-next-line unused-imports/no-unused-vars
export interface ValueFilter<V> {}

export interface SelectFilter<V, M = object> {
  options: FilterOption<V, M>[]
}

export interface MultiSelectFilter<V, M = object> {
  options: FilterOption<V, M>[]
}

export type SelectedValue<T> = T extends ValueFilter<infer U> ? U : never
export type SelectedOptionValue<T> = T extends SelectFilter<infer U> ? U : never
export type SelectedMultiOptionValues<T> = T extends MultiSelectFilter<infer U> ? U[] : never

export type SelectedOption<T> = T extends SelectFilter<infer U, infer M> ? FilterOption<U, M>[] : never
export type SelectedMultiOption<T> = T extends MultiSelectFilter<infer U, infer M> ? FilterOption<U, M>[] : never

export type FiltersSelection<F> = Partial<{
  [K in keyof F]: SelectedValue<F[K]> | SelectedOptionValue<F[K]> | SelectedMultiOptionValues<F[K]>;
}>

export interface SearchCriteria<F> {
  pagination?: SearchPagination
  sorting?: SearchSorting
  filterSelection?: FiltersSelection<F>
}

export type Participants = ValueFilter<RoomsParticipantDistribution>

/**
 * Search types
 */
export interface SearchValueFilters {
  PricePerPerson: ValueFilter<[number, number | null]>
  Participants: Participants
}

export interface SearchSelectFilters {
  Lastminute: SelectFilter<string, { DaysToDepartureRange: string }>
  Stars: SelectFilter<string>
  MetersToBeach: SelectFilter<string>
  MetersToCenter: SelectFilter<string>
  RoomCount: SelectFilter<string>
  TransportType: SelectFilter<string>
  CustomersRating: SelectFilter<string>
}

export const SingleSelectKeys = [
  'Lastminute',
  'Stars',
  'MetersToBeach',
  'MetersToCenter',
  'RoomCount',
  'TransportType',
  'CustomersRating',
] as (keyof SearchSelectFilters)[]

export interface SearchMultiSelectFilters {
  AccommodationType: MultiSelectFilter<string>
  ArrivalAirport: MultiSelectFilter<string>
  Campaigns: MultiSelectFilter<string>
  ChildrenFacilities: MultiSelectFilter<string>
  City: MultiSelectFilter<string, { ParentId: string }>
  Country: MultiSelectFilter<string>
  Collection: MultiSelectFilter<string>
  DepartureDate: MultiSelectFilter<string>
  DepartureAirport: MultiSelectFilter<string>
  Discount: MultiSelectFilter<string>
  Duration: MultiSelectFilter<string>
  Facilities: MultiSelectFilter<string>
  InternetFacilities: MultiSelectFilter<string>
  Mealplan: MultiSelectFilter<string>
  Region: MultiSelectFilter<string, { ParentId: string }>
  Themes: MultiSelectFilter<string>
  CampaignsAndDiscounts: MultiSelectFilter<string>
}

export type SearchFilters = Partial<SearchValueFilters & SearchSelectFilters & SearchMultiSelectFilters>

export interface SearchResponse {
  results: SearchResult[]
  totalResults: number
  pagination: SearchPagination
  filters: SearchFilters
  filteredCollection?: {
    collection: Collection
    description: string
    image: string
    title: string
    usps: string[]
  }
  accommodationTips: AccommodationTip[]
  ga4FilterCollection: GA4FilterCollectionEvent
  ga4Impressions: unknown[]
}

export interface SearchPagination {
  offset: number
  limit: number
}

export interface SearchSorting {
  by: string
}

export type CollectionId =
  | 'atlantis'
  | 'boutique'
  | 'cycling'
  | 'excellent'
  | 'famfun'
  | 'primavera'
  | 'secrets'
  | 'selections'
  | 'totally_snow'
  | 'villages'
  | 'xperience'

export interface Collection {
  descriptionForAccommodationPage: string
  id: CollectionId
  image: Image
  name: string
  text: string
  titleForAccommodationPage: string
  usps: string[]
}

export interface CollectionTeaser {
  collection: Collection
  image: string
  text: string
  title: string
  usps: string[]
}

export type SearchFilterSelection = FiltersSelection<SearchFilters>

export type SearchValueFilterType = keyof SearchValueFilters
export type SearchSelectFilterType = keyof SearchSelectFilters
export type SearchMultiSelectFilterType = keyof SearchMultiSelectFilters

export type SearchFilterType = keyof SearchFilters

/**
 * API types
 */
export interface SearchAPIRawResponse {
  pagination: {
    offset: number
    limit: number
    totalResults: number
  }
  filters: SearchAPIFilter[]
  sortInfo: { property: string }
  results: SearchResult[]
  accoTips?: AccommodationTipsApiResponse[]
  collectionTeaser?: CollectionTeaser
}

export interface SearchAPIFilter {
  type: keyof SearchFilters
  isMandatory?: boolean
  isMultiselectable?: boolean
  parentFilterType?: string
  values: SearchAPIFilterValue[]
}

export interface SearchAPIFilterValue {
  isAvailable?: boolean
  isSelected: boolean
  caption: string
  value: string
  count?: number
  metadata?: string
  groupInfo?: string
}

export interface SearchResult {
  agnosticId: number
  amountOfBookingsText: string
  arrivalDateDetail: string
  callToAction: {
    url: string
    text: string
  }
  campaign?: {
    id: string
    title: string
    icon: string
    highlightedWord: string
  }
  cityName?: string
  collection?: Collection
  countryName?: string
  departureDateDetail: string
  deskId: number
  englishName?: string
  featuredFilters?: {
    duration: string
    occupancy: string
    mealplan: string
  }
  geolocation: {
    latitude: number
    longitude: number
  }
  highestRatedCategory: {
    name: number
    rating: number
  }
  id: number
  images: string[]
  isFlightOnly: boolean
  isFavorite: boolean
  isHighlighted: boolean
  isOutOfSeason?: boolean
  isUnavailable: boolean
  lastBookingDateText: string
  locations: SearchLocation[]
  mostBookedInDestinationText: string
  name?: string
  occupancyInfo?: {
    adultsCount: number
    childrenCount: number
    babiesCount: number
  }
  price?: {
    averagePrice: number
    publicPrice?: {
      price?: number
      discountPercentage: number
    }
    skiPassPublicPriceText?: string
    priceDiscounts?: {
      originalPrice: number
      discountPercentage: number
      discountType?: string
    }
    currencySettings: {
      symbol: string
      symbolPosition: string
      numberOfDecimals: number
    }
    includedServices?: IncludedServices
    acmInformation?: {
      transportType: string
      departureDateRaw: string
      durationRaw: number
      birthdates?: string[]
      mealplanCode?: string
      location?: string
      acmUrl: string
    }
    yieldCampaignLogo?: {
      alt: string
      border: string
      class: string
      height: number
      hSpace: number
      src: string
      vSpace: number
      width: number
      mediaId: string
      title: string
      language: {
        Name: string
      }
      mediaExists: boolean
    }
    mandatoryExtraCostsText?: string
    staticText?: string
    yieldCampaignId?: string
    yieldCampaignName?: string
    onRequest: boolean
  }
  isMarketExclusive: boolean
  isSustainable: boolean
  rating?: number
  regionName?: string
  themes: string[]
  transportType: string
  url: string
  urlWithFilters: string
  userRating: number
  userRatingText: string
  usps: string[]
  outboundDepartureAirportName: string
}

export interface AccommodationTipsApiResponse {
  arrivalDateDetail: string
  countryName: string
  departureDateDetail: string
  featuredFilters: {
    occupancy: string
    mealplan: string
  }
  id: number
  images: string[]
  locations: SearchLocation[]
  name: string
  price?: {
    acmInformation: {
      departureDateRaw: string
      durationRaw: number
    }
    averagePrice: number
  }
  rating: number
  transportType: string
  urlWithFilters: string
  userRating: number
  userRatingText: string
  outboundDepartureAirportName: string
}

export interface AccommodationTip {
  arrivalDate: string
  departureDate: string
  departureDateRaw?: string
  durationRaw?: number
  id: number
  images: string[]
  locations: SearchLocation[]
  mealPlan: string
  name: string
  occupancy: string
  price?: number
  rating: number
  transportType: string
  url: string
  userRating: number
  userRatingText: string
  outboundDepartureAirportName: string
}

export interface IncludedServices {
  accommodation?: string
  bus?: string
  carrental?: string
  flight?: string
  skimaterial?: string
  skipass?: string
  transfer?: string
}

export interface SearchLocation {
  deskId: number
  name?: string
  type?: 'country' | 'region' | 'subregion' | 'city'
  url?: string
}

interface Image {
  alt: string
  attributes: string
  brand: string
  dynamicRatios: Ratio[]
  extraClasses: string
  keepOriginalSize: boolean
  placeholderSrc: string
  ratio: Ratio
  resolve: string
  resolvedSrc: string
  sync: boolean
  title: string
  track: string
}

interface Ratio {
  breakpoint: string
  hasRatio: boolean
  height: number
  width: number
}

export interface GA4FilterCollectionEvent {
  'partyComposition'?: string
  'country'?: string
  'region'?: string
  'city'?: string
  'departureDate'?: string
  'duration'?: string
  'price'?: string
  'airports'?: string
  'boardType'?: string
  'number of rooms'?: string
  'bathroomCount'?: string
  'general facility'?: string
  'internet facility'?: string
  'children facility'?: string
  'recommended'?: string
  'campaigns'?: string
  'themes'?: string
  'departureWeeks'?: string
  'accoType'?: string
  'ratings'?: string
  'distance from city center'?: string
  'distance from sea'?: string
  'discount'?: string
  'beds in livingroom'?: string
  'has availability'?: string
  'flight only'?: string
  'stars'?: string
  'labels'?: string
  'transport'?: string
  'airline'?: string
  'destination'?: string
  'arrival airports'?: string
}
